/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/*
* ------------------------------------------------------------------------------------------------------------
* Workaround to enable Cart Drawer trigger and Refresh on Product Add with Globo Product Options
* ------------------------------------------------------------------------------------------------------------
*/
document.addEventListener("DOMContentLoaded", () => {
  // Select the node that will be observed for mutations
  const cartBadge = document.querySelector(".header__cart-count.bubble-count");

  // Get the initial inner text of cartBadge
  let oldInnerText = cartBadge.innerText;

  // Options for the observer (which mutations to observe)
  const config = { childList: true, characterData: true };

  // Callback function to execute when mutations are observed
  const mutationCallback = (mutationList, observer) => {
    for (const mutation of mutationList) {
      // Perform the following checks: that the innerText has changed (first 4 conditions) and that the cart is not open:
      if (
        mutation.type === 'childList' 
        && mutation.target === cartBadge 
        && mutation.addedNodes.length
        && oldInnerText !== cartBadge.innerText
        && !document.getElementById('mini-cart').open
      ) {
          // const pseudoCartObject = {
          //   item_count: cartBadge.innerText
          // }
          // document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          //   bubbles: false,
          //   detail: {
          //     cart: pseudoCartObject
          //   }
          // }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: false
          }));
          document.getElementById('mini-cart').open = false;
        }
    }
  };

  // Create an observer instance linked to the callback function
  const observer = new MutationObserver(mutationCallback);
  
  // Start observing the target node for configured mutations
  observer.observe(cartBadge, config);
});

/*
* ------------------------------------------------------------------------------------------------------------
* Workaround to update Cart Drawer content when items with Globo options are changed.
* ------------------------------------------------------------------------------------------------------------
*/

document.addEventListener('cart:updated', autoRebalanceCartDrawer);
window.addEventListener('load', autoRebalanceCartDrawer);

async function autoRebalanceCartDrawer(event) {

  const response = await fetch(window.Shopify.routes.root + 'cart.js', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });
  const cart = await response.json();
  const lineItems = cart.items;
  const globoParents = [];
  const globoAddOns = [];
  // Iterate through line items
  for (const item of lineItems) {
    if (item.properties && item.properties.hasOwnProperty('_gpo_product_group')) {
      // For each one, check if they have the "_gpo_product_group" property. If they do, push them to the parent array 
      // Doing this will give us a list of items with add ons in the cart
      let globoId = item.properties._gpo_product_group;
      globoParents.push({
        globoId: globoId,
        quantity: item.quantity
      });
    } else if (item.properties && item.properties.hasOwnProperty('_gpo_parent_product_group')) {
      // Else, check if they have the "_gpo_parent_product_group" property. If they do, push them to the add on array 
      // Doing this will give us a list of add ons in the cart
      let globoId = item.properties._gpo_parent_product_group;
      globoAddOns.push({
        globoId: globoId,
        lineItemKey: item.key,
        quantity: item.quantity
      });
    }
  };

  // Next steps will be to compare quantities of matching globoIds in the 2 arrays and make an ajax call to update depending on the result.
  // Check if the globo ID is still in cart or exists (by checking the parent array). If not, remove the add on. 
  removeItemsNotInParentArray(globoParents, globoAddOns);
  checkQuantityDifference(globoParents, globoAddOns);

}

function changeCartQuantity(lineKey, quantity) {
  const data = {
    id: lineKey,
    quantity: quantity
  }

  fetch(window.Shopify.routes.root + 'cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
  .then(async response => {
    const cartContent = await response.json();
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", {
      bubbles: true,
      detail: {
        cart: cartContent
      }
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: {
        cart: cartContent,
        replacementDelay: quantity === 0 ? 600 : 750
      }
    }));
  })
  .catch((error) => {
    console.error('Error:', error);
  });
} 

function checkQuantityDifference(globoParents, globoAddOns) {
  // Loop through the objects in the add ons array
  for (const globoAddOn of globoAddOns) {
    // Loop through the objects in the parents array
    for (const globoParent of globoParents) {
      // If there are matching globo Ids, it means both the parent and add on are still in the cart and their quantity should be the same
      if (globoParent.globoId === globoAddOn.globoId && globoParent.quantity !== globoAddOn.quantity) {
        // Change add on quantity to be the same as parent item quantity
        globoAddOn.quantity = globoParent.quantity;
        // Make an AJAX call to push the quantity update to cart.js
        changeCartQuantity(globoAddOn.lineItemKey, globoAddOn.quantity)
      }
    }
  }
}

function removeItemsNotInParentArray(globoParents, globoAddOns) {
  // Loop through the objects in the Add Ons array
  for (const globoAddOn of globoAddOns) {
    // Check if the globo Id from the Add Ons array exists in the parent array. 
    const matchingGloboParent = globoParents.find((parent) => parent.globoId === globoAddOn.globoId);
    if (!matchingGloboParent) {
      // make AJAX call to remove the item that corresponds to the node if it doesn't exist
      changeCartQuantity(globoAddOn.lineItemKey, 0)
    }
  }
}

/*
* ------------------------------------------------------------------------------------------------------------
* Swatch availability on product page
* ------------------------------------------------------------------------------------------------------------
*/


document.addEventListener("DOMContentLoaded", async () => {

  if (document.querySelector('.color-swatch-list')) {
    const swatchContainer = document.querySelector('.color-swatch-list');
    const swatchLinks = swatchContainer.querySelectorAll('a');

    const swatchList = [];
    
    swatchLinks.forEach(async (a) => {
      swatchList.push({
        "handle": a.getAttribute('href').replace('/products/','')
      });
    });
    
    await Promise.all(swatchList.map(async (swatch) => {
      swatch.available = await getProductAvailability(swatch.handle)
    }));

    updateUnavailableSwatches();
  
    function getProductAvailability(handle) {
      return fetch(window.Shopify.routes.root + `products/${handle}.js`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          return data.available;
        } else {
          return false
        }
      })
    }

    function updateUnavailableSwatches() {
      // Iterate through hrefValues array, for each handle, check if it's unavailable. 
      for (const swatch of swatchList) {
        if (swatch.available == false) {
          for (const swatchNode of swatchLinks) {
            // If it is, look for the corresponding anchor tag with the href value matching the handle. 
            if (swatch.handle == swatchNode.getAttribute('href')) {
              const swatchDiv = swatchNode.querySelector('.color-swatch');
              // Add the "is-disabled" class to that node
              swatchDiv.classList.add('is-disabled');
            }
          }
        }
      }
    }
  }
  
});

/*
* ------------------------------------------------------------------------------------------------------------
* Remove empty p-tags in Tab Content in Product Page
* ------------------------------------------------------------------------------------------------------------
*/
document.addEventListener("DOMContentLoaded", () => {
  const tabContents = document.querySelectorAll('.product-tabs__tab-item-content')
  tabContents.forEach((tabContent) => {
    tabContent.querySelectorAll("*:empty").forEach((x) => {
      x.remove()
    });
  });
});

/*
* ------------------------------------------------------------------------------------------------------------
* Hide or display Notify Me Button on Product Page -- Replaced with CSS on product-form
* ------------------------------------------------------------------------------------------------------------
*/
document.addEventListener("DOMContentLoaded", function() {
    // Get the AddToCart button and NotifyMe button
    const addToCartButton = document.getElementById("AddToCart");
    const notifyMeButton = document.getElementById("NotifyMeBtn");
    // Function to check the disabled state of AddToCart and toggle NotifyMe visibility
    function toggleNotifyMeButton(e) {
        if (e === undefined || addToCartButton) {
            if (addToCartButton.hasAttribute("disabled")) {
                // If AddToCart is disabled, show the NotifyMe button
                if (notifyMeButton) {
                    notifyMeButton.style.display = "inline-block";
                }
            } else if (e === undefined || !addToCartButton) {
                // If AddToCart is not disabled, hide the NotifyMe button
                if (notifyMeButton) {
                    notifyMeButton.style.display = "none";
                }
            } else if (e !== undefined && (e?.target.classList.contains("block-swatch__item") && e?.target.parentElement.classList.contains("is-disabled"))) {
              if (notifyMeButton) {
                    notifyMeButton.style.display = "inline-block";
              }
            }
        }
    }
    // Listen for clicks on any label node with the class block-swatch
    const swatchItems = document.querySelectorAll(".block-swatch__item");
    swatchItems.forEach(function(item) {
        item.addEventListener("click", function(e) {
            toggleNotifyMeButton(e);
        });
    });
    // Initial check on page load
    toggleNotifyMeButton();
});

/*
* ------------------------------------------------------------------------------------------------------------
* Change PDP CTA when color variant is changed
* ------------------------------------------------------------------------------------------------------------
*/



document.addEventListener("DOMContentLoaded", function() {
    //Check if the color variant exists on the PDP
    const colorVariantsArr = document.querySelector('.custom-color-swatches');

    if (colorVariantsArr) {
          changeCtaAndAnnouncementColor();
    }
    


    function changeCtaAndAnnouncementColor(e) {
        //Check if the active swatch has a background-color inline styling
        const checkedRadio = document.querySelector('.color-swatch__radio[checked="checked"]');
        const parent = checkedRadio ? checkedRadio.closest('.color-swatch') : null;
        const colorSwatchItem = parent.querySelector('.color-swatch__item');
        const backgroundColor = colorSwatchItem.style.backgroundColor;
        const atcButton = document.querySelector('#AddToCart');
        const stickyAtcButton = document.querySelector('#StickyAddToCart');
        const announcementBar = document.querySelector('.announcement-bar');
        const soldOutButton = document.querySelector('#NotifyMeBtn');
    
        
        if (backgroundColor) {
          const rgbArr = backgroundColor.match(/\d+/g).map(Number);
          const color = Math.round(((parseInt(rgbArr[0]) * 299) + 
            (parseInt(rgbArr[1]) * 587) + 
            (parseInt(rgbArr[2]) * 114)) / 1000);
          const textColor = (color > 125) ? 'black' : 'white'; 

          atcButton.style.backgroundColor = backgroundColor;
          atcButton.style.color = textColor;
          stickyAtcButton.style.backgroundColor = backgroundColor;
          stickyAtcButton.style.color = textColor;
          announcementBar.style.backgroundColor = backgroundColor;
          announcementBar.style.color = textColor;
          soldOutButton.style.backgroundColor = backgroundColor;
          soldOutButton.style.color = textColor;
        } 

        return
    }


});

// const disableHour = 23;
// const disableMinute = 59;
// const disableSecond = 58;

// const refreshHour = 0;
// const refreshMinute = 0;
// const refreshSecond = 0;

// const disableCheckoutButtons = () => {
//   document.querySelectorAll('.checkout-button').forEach(button => {
//     button.disabled = true;
//     button.classList.add('disabled');

//     // Optional visual lock
//     button.style.pointerEvents = 'none';
//     button.style.opacity = '0.5';
//     button.style.cursor = 'not-allowed';
//   });

//   console.log('🚫 Checkout buttons disabled');
// };

// const refreshPage = () => {
//   console.log('🔄 Refreshing page at 12:00:00 AM');
//   location.reload();
// };

// const interval = setInterval(() => {
//   const now = new Date();

//   // Disable checkout buttons at 11:59:40 PM
//   if (
//     now.getHours() === disableHour &&
//     now.getMinutes() === disableMinute &&
//     now.getSeconds() >= disableSecond
//   ) {
//     disableCheckoutButtons();
//   }

//   // Refresh page at 12:00:00 AM
//   if (
//     now.getHours() === refreshHour &&
//     now.getMinutes() === refreshMinute &&
//     now.getSeconds() >= refreshSecond
//   ) {
//     clearInterval(interval);
//     refreshPage();
//   }

// }, 250);

/*
* ------------------------------------------------------------------------------------------------------------
* Checkout gate: strip orphaned August Bundle parent lines before checkout
*
* A bundle parent (properties.Role === "Parent") is only complete once
* cart-bundle-fulfill.js has added its Role: "Child" lines for the same
* "Bundle ID" — that add happens in a second request, after the parent line
* already exists (see cart-bundle-fulfill.js). If that request never lands
* (network hiccup, ad blocker, tab closed mid-flow, sessionStorage not
* shared across tabs), the parent sits in the cart alone: paid at full
* bundle price with no set pieces to fulfill. Its paired "Item
* Personalization" line (matched via the shared "_pc_pricing_ref" property)
* is production data for that same missing garment, so it's removed with it.
*
* Runs on every checkout submit (cart page recap form and mini-cart drawer
* form both use a button named "checkout") so a broken parent never reaches
* Shopify's checkout.
* ------------------------------------------------------------------------------------------------------------
*/
// function findOrphanBundleLines(cart) {
//   const orphanKeys = new Set();
//   const removedTitles = new Set();

//   const parents = cart.items.filter((item) => item.properties?.Role === 'Parent' && item.properties?.['Bundle ID']);

//   for (const parent of parents) {
//     const bundleId = parent.properties['Bundle ID'];
//     const hasChild = cart.items.some(
//       (item) => item.properties?.Role === 'Child' && item.properties?.['Bundle ID'] === bundleId
//     );
//     if (hasChild) continue;

//     orphanKeys.add(parent.key);
//     removedTitles.add(parent.product_title || parent.title || parent.name || 'Item');

//     const pricingRef = parent.properties._pc_pricing_ref;
//     if (pricingRef) {
//       cart.items.forEach((item) => {
//         if (item.key !== parent.key && item.properties?._pc_pricing_ref === pricingRef) {
//           orphanKeys.add(item.key);
//         }
//       });
//     }
//   }

//   return { orphanKeys, removedTitles };
// }

// async function runCheckoutGate() {
//   const cart = await fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
//   const { orphanKeys, removedTitles } = findOrphanBundleLines(cart);

//   if (orphanKeys.size === 0) return null;

//   for (const key of orphanKeys) {
//     await fetch(window.Shopify.routes.root + 'cart/change.js', {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json' },
//       body: JSON.stringify({ id: key, quantity: 0 })
//     });
//   }

//   const updatedCart = await fetch(window.Shopify.routes.root + 'cart.js').then((res) => res.json());
//   document.documentElement.dispatchEvent(
//     new CustomEvent('cart:updated', { bubbles: true, detail: { cart: updatedCart } })
//   );
//   document.documentElement.dispatchEvent(
//     new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: updatedCart, replacementDelay: 600 } })
//   );

//   return removedTitles;
// }

// let checkoutGateTimer = null;

// function clearCheckoutGateTimer() {
//   if (checkoutGateTimer) {
//     clearInterval(checkoutGateTimer);
//     checkoutGateTimer = null;
//   }
// }

// function hideCheckoutGateModal() {
//   clearCheckoutGateTimer();
//   document.getElementById('checkout-gate-modal-overlay')?.classList.remove('is-open');
// }

// // Cleaned cart still needs the customer's original checkout intent honored, so
// // this auto-continues to checkout after a short countdown instead of just
// // silently closing — "Review cart" is there for anyone who wants to catch it.
// function showCheckoutGateModal(removedTitles, form) {
//   const overlay = document.getElementById('checkout-gate-modal-overlay');
//   const list = document.getElementById('checkout-gate-modal__list');
//   const countdown = document.getElementById('checkout-gate-modal__countdown');
//   if (!overlay || !list) return;

//   list.innerHTML = '';
//   removedTitles.forEach((title) => {
//     const row = document.createElement('div');
//     row.className = 'checkout-gate-modal__item-row';
//     row.textContent = title;
//     list.appendChild(row);
//   });

//   overlay.classList.add('is-open');

//   let secondsLeft = 20;
//   const renderCountdown = () => {
//     if (countdown) countdown.textContent = `Redirecting to checkout in ${secondsLeft}s`;
//   };
//   renderCountdown();

//   clearCheckoutGateTimer();
//   checkoutGateTimer = setInterval(() => {
//     secondsLeft -= 1;
//     if (secondsLeft <= 0) {
//       clearCheckoutGateTimer();
//       overlay.classList.remove('is-open');
//       form.submit();
//       return;
//     }
//     renderCountdown();
//   }, 1000);
// }

// document.addEventListener('DOMContentLoaded', () => {
//   const overlay = document.getElementById('checkout-gate-modal-overlay');
//   if (!overlay) return;

//   overlay.querySelectorAll('[data-checkout-gate-dismiss]').forEach((button) => {
//     button.addEventListener('click', hideCheckoutGateModal);
//   });

//   overlay.addEventListener('click', (event) => {
//     if (event.target === overlay) hideCheckoutGateModal();
//   });
// });

// document.addEventListener(
//   'submit',
//   (event) => {
//     if (event.submitter?.name !== 'checkout') return;

//     event.preventDefault();
//     const form = event.target;

//     runCheckoutGate()
//       .then((removedTitles) => {
//         if (removedTitles) {
//           showCheckoutGateModal(removedTitles, form);
//           return;
//         }
//         form.submit();
//       })
//       .catch((error) => {
//         console.error('[Checkout Gate] Failed to validate cart, letting checkout proceed:', error);
//         form.submit();
//       });
//   },
//   true
// );